import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  Shield, Menu, X, Wifi, WifiOff, Search, LogOut,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useRealtimeStore } from '@/hooks/useRealtime'
import { clsx } from 'clsx'
import { NotificationBell } from '@/components/ui/NotificationDrawer'
import { useNavGroups } from '@/hooks/useNavGroups'

export default function TopNav({
  onSearchClick,
  onNotifClick,
}: {
  onSearchClick?: () => void
  onNotifClick: () => void
}) {
  const [mobileOpen, setMobileOpen] = useState(false)
  // The mobile menu was a flat scroll of ~93 links — every group, every
  // item, no collapse, no search — on the surface where scrolling costs
  // most. Desktop's SideNav has both; this brings the phone to parity.
  // Groups start collapsed except the wife-safe Overview zone.
  const [navQuery, setNavQuery] = useState('')
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(['overview']))
  const toggleGroup = (id: string) =>
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const connected = useRealtimeStore(s => s.connected)
  // Single nav model, capability-filtered — pages this user's tier can't
  // access (would 403 server-side) never appear in the mobile menu.
  const navGroups = useNavGroups()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header className="glass md:hidden sticky top-0 z-40 border-0 border-b border-border">
      <div className="flex items-center justify-between px-4 h-14">
        {/* Logo */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-grad-blue flex items-center justify-center shrink-0 shadow-lg shadow-blue/20">
            <Shield size={15} className="text-white" aria-hidden />
          </div>
          <span className="flex items-baseline gap-1.5 min-w-0">
            <span className="font-display font-bold text-sm text-text-primary">AgentVault</span>
            <span className="text-[9px] uppercase tracking-[0.14em] text-text-muted truncate">Capital</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <button
            type="button"
            onClick={onSearchClick}
            className="p-1.5 rounded-lg hover:bg-surface-card transition-colors"
            aria-label="Search"
          >
            <Search size={18} className="text-text-secondary" />
          </button>

          {/* WS status indicator */}
          <span
            title={connected ? 'Live feed connected' : 'Live feed disconnected'}
            aria-label={connected ? 'Live feed connected' : 'Disconnected'}
          >
            {connected
              ? <Wifi size={16} className="text-emerald" />
              : <WifiOff size={16} className="text-text-muted" />}
          </span>

          {/* Notification bell */}
          <NotificationBell onClick={onNotifClick} />

          {/* Hamburger */}
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen ? 'true' : 'false'}
            onClick={() => setMobileOpen(o => !o)}
            className="p-1.5 rounded-lg hover:bg-surface-card transition-colors"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-surface-DEFAULT px-3 py-3 animate-slide-in max-h-[80dvh] overflow-y-auto">
          {/* Full grouped nav — same single nav model as the desktop
              SideNav (useNavGroups), already capability-filtered so no
              entry here can 403 for this user. */}
          <div className="px-1 pb-2">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-card border border-border">
              <Search size={14} className="text-text-muted shrink-0" aria-hidden />
              <input
                value={navQuery}
                onChange={e => setNavQuery(e.target.value)}
                placeholder="Find a page…"
                className="bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none w-full"
              />
            </div>
          </div>
          {navGroups
            .map(group => {
              const q = navQuery.trim().toLowerCase()
              if (!q) return { group, items: group.items, searching: false }
              const items = group.items.filter(it =>
                it.label.toLowerCase().includes(q)
                || (it.tooltip ?? '').toLowerCase().includes(q)
                || (it.keywords ?? []).some(k => k.toLowerCase().includes(q)))
              return { group, items, searching: true }
            })
            .filter(({ items }) => items.length > 0)
            .map(({ group, items, searching }) => (
            <div key={group.id} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center justify-between px-3 pt-2 pb-1 panel-title"
                aria-expanded={searching || openGroups.has(group.id)}
              >
                {group.title}
                <span aria-hidden>{searching || openGroups.has(group.id) ? '−' : '+'}</span>
              </button>
              <div className={clsx('nav-roll', (searching || openGroups.has(group.id)) && 'nav-roll-open')}>
              <div className="nav-roll-inner space-y-0.5">
                {items.map(({ to, label, icon: Icon, end, tooltip }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    title={tooltip}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-3 rounded-lg overflow-visible text-sm transition-colors min-h-[44px]',
                        isActive
                          ? 'nav-active'
                          : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                      )
                    }
                  >
                    {Icon && <Icon size={16} aria-hidden />}
                    {label}
                  </NavLink>
                ))}
              </div>
              </div>
            </div>
          ))}
          <div className="pt-2 border-t border-border mt-2">
            {user && (
              <div className="px-3 py-1 flex items-center gap-2">
                <p className="text-xs text-text-muted truncate">{user.name}</p>
                {user.permissions?.can_configure === false && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue/10 text-blue-light border border-blue/30">
                    Demo
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                         text-text-secondary hover:bg-rose/10 hover:text-rose-light transition-colors"
            >
              <LogOut size={16} aria-hidden />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </header>
  )
}
